import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { buildPublicLpDestinationHref, getSafePublicLpRef } from "../../lib/publicLpReturn";

export function buildRegistrationHref(search: string): string {
  const ref = getSafePublicLpRef(search);
  return ref
    ? `/register?ref=${encodeURIComponent(ref)}`
    : "/register";
}

export function PublicLpLogo() {
  return (
    <span className="inline-flex items-center gap-2.5 font-black tracking-tight text-[#5D4037]">
      <img
        src="/img/wizmate-mark.png"
        width="40"
        height="40"
        alt=""
        className="h-10 w-10 rounded-xl object-contain"
      />
      <span className="text-xl">WizMate</span>
    </span>
  );
}

const DEFAULT_PUBLIC_LP_NAVIGATION = [
  { href: "#workflow", label: "使い方" },
  { href: "#president-room", label: "AI社長室とは" },
  { href: "#pricing", label: "料金" },
] as const;

export function PublicLpHeader({
  registrationHref,
  navigation = DEFAULT_PUBLIC_LP_NAVIGATION,
  navigationLabel = "営業支援LP",
}: {
  registrationHref: string;
  navigation?: readonly { href: string; label: string }[];
  navigationLabel?: string;
}) {
  return (
    <header className="sticky top-0 z-40 border-b border-[#EADBC8]/80 bg-[#FDF8F5]/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-5 md:px-8">
        <Link to="/" aria-label="WizMate トップへ">
          <PublicLpLogo />
        </Link>
        <nav aria-label={navigationLabel} className="hidden items-center gap-6 text-sm font-bold text-[#6B5851] md:flex">
          {navigation.map(({ href, label }) => (
            <a key={href} href={href} className="transition hover:text-[#A05F3F]">{label}</a>
          ))}
        </nav>
        <Link
          to={registrationHref}
          className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[#8A4F35] px-4 text-sm font-black text-white shadow-[0_8px_20px_rgba(138,79,53,0.22)] transition hover:bg-[#74412C] focus:outline-none focus:ring-2 focus:ring-[#C58B6B] focus:ring-offset-2"
        >
          無料で試す
        </Link>
      </div>
    </header>
  );
}

export function SectionIntro({
  eyebrow,
  title,
  children,
  align = "left",
  contentClassName = "max-w-3xl",
}: {
  eyebrow: string;
  title: string;
  children?: ReactNode;
  align?: "left" | "center";
  contentClassName?: string;
}) {
  const alignment = align === "center" ? "mx-auto text-center" : "";
  return (
    <div className={`max-w-5xl ${alignment}`}>
      <p className="text-xs font-black tracking-[0.2em] text-[#B06E4F]">{eyebrow}</p>
      <h2 className="mt-3 text-2xl font-black leading-snug tracking-tight text-[#4E3830] md:text-4xl">
        {title}
      </h2>
      {children ? <div className={`mt-5 text-base leading-8 text-[#6B5851] ${contentClassName} ${align === "center" ? "mx-auto" : ""}`}>{children}</div> : null}
    </div>
  );
}

export function BrowserMockFrame({
  title,
  caption,
  children,
}: {
  title: string;
  caption: string;
  children: ReactNode;
}) {
  return (
    <figure>
      <div className="overflow-hidden rounded-[1.6rem] border border-[#DCC6B4] bg-white shadow-[0_24px_54px_rgba(103,73,58,0.16)]">
        <div className="flex min-h-11 items-center gap-2 border-b border-[#EADBC8] bg-[#F7EEE7] px-4">
          <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-[#DB8A76]" />
          <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-[#E7BE79]" />
          <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-[#8BB99D]" />
          <span className="ml-2 truncate text-xs font-bold text-[#7B6861]">{title}</span>
          <span className="ml-auto rounded-full bg-white px-2.5 py-1 text-[10px] font-black text-[#9A7968]">
            画面イメージ
          </span>
        </div>
        <div className="p-4 sm:p-6">{children}</div>
      </div>
      <figcaption className="mt-3 text-center text-xs leading-6 text-[#8B756D]">{caption}</figcaption>
    </figure>
  );
}

export function ProductScreenshot({
  src,
  alt,
  width,
  height,
  caption,
  frameTitle,
  cropClassName = "",
  figureClassName = "",
}: {
  src: string;
  alt: string;
  width: number;
  height: number;
  caption: string;
  frameTitle: string;
  cropClassName?: string;
  figureClassName?: string;
}) {
  return (
    <figure className={figureClassName}>
      <div className="overflow-hidden rounded-[1.6rem] border border-[#DCC6B4] bg-[#FFFDFC] shadow-[0_24px_54px_rgba(103,73,58,0.16)]">
        <div className="flex min-h-11 items-center gap-2 border-b border-[#EADBC8] bg-[#F7EEE7] px-4">
          <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-[#DB8A76]" />
          <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-[#E7BE79]" />
          <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-[#8BB99D]" />
          <span className="ml-2 truncate text-xs font-bold text-[#7B6861]">{frameTitle}</span>
          <span className="ml-auto rounded-full bg-white px-2.5 py-1 text-[10px] font-black text-[#9A7968]">実画面</span>
        </div>
        <div className={cropClassName || undefined}>
          <img
            src={src}
            alt={alt}
            width={width}
            height={height}
            loading="lazy"
            decoding="async"
            className={cropClassName ? "h-full w-full object-cover object-[center_61%]" : "block h-auto w-full"}
          />
        </div>
      </div>
      <figcaption className="mt-3 text-center text-xs leading-6 text-[#8B756D]">{caption}</figcaption>
    </figure>
  );
}

export function PublicLpFooter({ returnTo = null }: { returnTo?: string | null }) {
  return (
    <footer className="border-t border-[#EADBC8] bg-[#F7EEE7]">
      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-10 md:grid-cols-[1.4fr_1fr] md:px-8">
        <div>
          <PublicLpLogo />
          <p className="mt-3 max-w-md text-sm leading-7 text-[#75625B]">
            社長の毎日に、AI社長室を。
          </p>
        </div>
        <nav aria-label="法務・サポート" className="flex flex-wrap items-start gap-x-5 gap-y-3 text-sm font-bold text-[#6B5851] md:justify-end">
          <Link to={buildPublicLpDestinationHref("/plans", returnTo)} className="underline-offset-4 hover:underline">料金</Link>
          <Link to={buildPublicLpDestinationHref("/terms", returnTo)} className="underline-offset-4 hover:underline">利用規約</Link>
          <Link to={buildPublicLpDestinationHref("/privacy", returnTo)} className="underline-offset-4 hover:underline">プライバシーポリシー</Link>
          <Link to={buildPublicLpDestinationHref("/commercial-transactions", returnTo)} className="underline-offset-4 hover:underline">特定商取引法に基づく表記</Link>
          <Link to="/support-inquiry" className="underline-offset-4 hover:underline">お問い合わせ</Link>
        </nav>
      </div>
    </footer>
  );
}
